import React, { useState, useEffect, useCallback } from 'react';
import { goalAPI } from '../services/api';
import { DashboardLayout } from '../components/DashboardLayout';
import { useGym } from '../contexts/GymContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Plus, Pencil, Trash2, Target, Wallet, TrendingUp, TrendingDown, ArrowUp, ArrowDown, Check, Loader2, Percent, CalendarDays, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const NAME_TO_REF = {
  'Faturamento': 'faturamento', 'ROI': 'roi', 'Margem de Lucro': 'margem_lucro',
  'Despesas': 'despesas', 'Lucro': 'lucro', 'Folha de Pagamento': 'folha_pagamento',
};
const ORCAMENTO_REFS = ['despesas', 'folha_pagamento'];
// Listas literais (o instrumentation de preview mantém arrays literais; exclui arrays derivados de .filter)
const META_TYPES = ['Faturamento', 'ROI', 'Margem de Lucro', 'Lucro', 'Novos Clientes', 'Retenção'];
const ORCAMENTO_TYPES = ['Despesas', 'Folha de Pagamento'];

export const GoalsPage = () => {
  const { selectedGym } = useGym();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [referenceValues, setReferenceValues] = useState(null);
  const [loadingReference, setLoadingReference] = useState(false);
  const [goalCategories, setGoalCategories] = useState([]);
  const [activeTab, setActiveTab] = useState('metas'); // 'metas' | 'orcamento'
  const [formData, setFormData] = useState({
    name: '',
    target_value: '',
    current_value: '',
    goal_type: 'Faturamento',
    direction: 'progressiva',
    is_percentage: false,
    percentage_value: '',
    reference_type: 'faturamento',
    period_type: 'mensal',
    month: new Date().toLocaleString('pt-BR', { month: 'long' }),
    year: new Date().getFullYear(),
    description: ''
  });

  const months = [
    'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
    'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'
  ];

  const allGoalTypes = goalCategories.length > 0
    ? goalCategories.map(cat => cat.name)
    : ['Faturamento', 'ROI', 'Margem de Lucro', 'Despesas', 'Lucro', 'Folha de Pagamento', 'Novos Clientes', 'Retenção'];

  const referenceTypes = [
    { value: 'faturamento', label: 'Faturamento' },
    { value: 'despesas', label: 'Despesas' },
    { value: 'lucro', label: 'Lucro' },
    { value: 'roi', label: 'ROI (%)' }
  ];

  // --- Classificação Meta vs Orçamento ---
  const refOfType = useCallback((type) => {
    const c = goalCategories.find(x => x.name === type);
    return c?.financial_reference || NAME_TO_REF[type] || null;
  }, [goalCategories]);

  const groupOfType = useCallback((type) => {
    const ref = refOfType(type);
    return ref && ORCAMENTO_REFS.includes(ref) ? 'orcamento' : 'metas';
  }, [refOfType]);

  const groupOfGoal = useCallback((goal) => {
    const ref = refOfType(goal.goal_type);
    if (ref) return ORCAMENTO_REFS.includes(ref) ? 'orcamento' : 'metas';
    return (goal.direction === 'decrescente') ? 'orcamento' : 'metas';
  }, [refOfType]);

  // Tipos disponíveis para a aba ativa
  const goalTypesForTab = activeTab === 'orcamento' ? ORCAMENTO_TYPES : META_TYPES;

  const isOrcamento = activeTab === 'orcamento';

  const loadReferenceValues = useCallback(async (month, year, periodType) => {
    if (!selectedGym) return;
    setLoadingReference(true);
    try {
      const response = await api.get('/goals/reference-values', {
        params: { gym_id: selectedGym.id, month, year, period_type: periodType || 'mensal' }
      });
      setReferenceValues(response.data);
    } catch (error) {
      setReferenceValues(null);
    } finally {
      setLoadingReference(false);
    }
  }, [selectedGym]);

  const loadGoals = useCallback(async () => {
    if (!selectedGym) return;
    try {
      const response = await goalAPI.getAll(selectedGym.id);
      setGoals(response.data);
    } catch (error) {
      toast.error('Erro ao carregar metas');
    } finally {
      setLoading(false);
    }
  }, [selectedGym]);

  const loadGoalCategories = useCallback(async () => {
    if (!selectedGym) return;
    try {
      const response = await goalAPI.getCategories(selectedGym.id);
      setGoalCategories(response.data);
    } catch (error) { /* ignore */ }
  }, [selectedGym]);

  useEffect(() => {
    if (selectedGym) {
      loadGoals();
      loadGoalCategories();
    }
  }, [selectedGym, loadGoals, loadGoalCategories]);

  useEffect(() => {
    if (selectedGym && formData.year && dialogOpen) {
      loadReferenceValues(formData.month, formData.year, formData.period_type);
    }
  }, [formData.month, formData.year, formData.period_type, selectedGym, dialogOpen, loadReferenceValues]);

  const getValueForGoalType = useCallback((goalType, refValues) => {
    if (!refValues) return null;
    const category = goalCategories.find(cat => cat.name === goalType);
    const isFin = category ? category.is_financial : !!NAME_TO_REF[goalType];
    if (!isFin) return null;
    const mapping = {
      'faturamento': refValues.faturamento,
      'roi': refValues.roi,
      'margem_lucro': refValues.margem_lucro,
      'despesas': refValues.despesas,
      'lucro': refValues.lucro,
      'folha_pagamento': refValues.despesas_por_categoria?.['Folha de Pagamento'] || 0,
    };
    const ref = category?.financial_reference || NAME_TO_REF[goalType];
    return ref ? mapping[ref] : null;
  }, [goalCategories]);

  const isFinancialGoalType = useCallback((goalType) => {
    const category = goalCategories.find(cat => cat.name === goalType);
    if (category) return category.is_financial || false;
    return !!NAME_TO_REF[goalType];
  }, [goalCategories]);

  // Auto-fill current_value based on goal_type
  useEffect(() => {
    if (referenceValues && formData.goal_type) {
      const value = getValueForGoalType(formData.goal_type, referenceValues);
      if (value !== null && value !== undefined) {
        setFormData(prev => ({ ...prev, current_value: value.toString() }));
      }
    }
  }, [formData.goal_type, referenceValues, getValueForGoalType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGym) return;
    setSaving(true);
    try {
      let targetValue = parseFloat(formData.target_value) || 0;
      if (formData.is_percentage && formData.percentage_value && referenceValues) {
        const percentage = parseFloat(formData.percentage_value);
        if (formData.reference_type === 'roi') {
          targetValue = percentage;
        } else {
          const referenceValue = referenceValues[formData.reference_type] || 0;
          targetValue = (percentage / 100) * referenceValue;
        }
      }

      const isAnual = formData.period_type === 'anual';
      const baseData = {
        gym_id: selectedGym.id,
        name: formData.name,
        target_value: targetValue,
        current_value: parseFloat(formData.current_value || 0),
        goal_type: formData.goal_type,
        direction: formData.direction,
        is_percentage: formData.is_percentage,
        percentage_value: formData.is_percentage ? parseFloat(formData.percentage_value) : null,
        reference_type: formData.is_percentage ? formData.reference_type : null,
        period_type: formData.period_type,
        year: formData.year,
        description: formData.description
      };

      if (editingGoal) {
        await goalAPI.update(editingGoal.id, { ...baseData, month: isAnual ? null : formData.month });
        toast.success(isOrcamento ? 'Orçamento atualizado!' : 'Meta atualizada!');
      } else if (isAnual) {
        await goalAPI.create({ ...baseData, month: null });
        toast.success(isOrcamento ? 'Orçamento anual criado!' : 'Meta anual criada!');
      } else {
        const monthsToCreate = selectedMonths.length > 0 ? selectedMonths : [formData.month];
        await Promise.all(monthsToCreate.map(month => goalAPI.create({ ...baseData, month })));
        toast.success(monthsToCreate.length > 1
          ? `${monthsToCreate.length} ${isOrcamento ? 'orçamentos criados' : 'metas criadas'}!`
          : (isOrcamento ? 'Orçamento criado!' : 'Meta criada!'));
      }

      loadGoals();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja deletar?')) {
      try {
        await goalAPI.delete(id);
        toast.success('Deletado com sucesso!');
        loadGoals();
      } catch (error) {
        toast.error('Erro ao deletar');
      }
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setSelectedMonths([]);
    setActiveTab(groupOfGoal(goal));
    setFormData({
      name: goal.name,
      target_value: goal.target_value?.toString() || '',
      current_value: goal.current_value?.toString() || '',
      goal_type: goal.goal_type,
      direction: goal.direction || 'progressiva',
      is_percentage: goal.is_percentage || false,
      percentage_value: goal.percentage_value?.toString() || '',
      reference_type: goal.reference_type || 'faturamento',
      period_type: goal.period_type || 'mensal',
      month: goal.month || new Date().toLocaleString('pt-BR', { month: 'long' }),
      year: goal.year,
      description: goal.description || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingGoal(null);
    setSelectedMonths([]);
    setReferenceValues(null);
    const orc = activeTab === 'orcamento';
    setFormData({
      name: '',
      target_value: '',
      current_value: '',
      goal_type: goalTypesForTab[0] || (orc ? 'Despesas' : 'Faturamento'),
      direction: orc ? 'decrescente' : 'progressiva',
      is_percentage: false,
      percentage_value: '',
      reference_type: orc ? 'despesas' : 'faturamento',
      period_type: 'mensal',
      month: new Date().toLocaleString('pt-BR', { month: 'long' }),
      year: new Date().getFullYear(),
      description: ''
    });
  };

  const openAdd = () => {
    resetForm();
    setEditingGoal(null);
    setDialogOpen(true);
  };

  const calculatedTarget = formData.is_percentage && formData.percentage_value && referenceValues
    ? formData.reference_type === 'roi'
      ? parseFloat(formData.percentage_value)
      : (parseFloat(formData.percentage_value) / 100) * (referenceValues[formData.reference_type] || 0)
    : null;

  const isROIReference = formData.reference_type === 'roi';

  const toggleMonth = (month) => {
    setSelectedMonths(prev => prev.includes(month) ? prev.filter(m => m !== month) : [...prev, month]);
  };
  const selectAllMonths = () => {
    setSelectedMonths(selectedMonths.length === months.length ? [] : [...months]);
  };

  const isGoalAchieved = (goal) => {
    const direction = goal.direction || 'progressiva';
    return direction === 'progressiva' ? goal.current_value >= goal.target_value : goal.current_value <= goal.target_value;
  };
  const calculateProgress = (goal) => {
    if (!goal.target_value) return 0;
    return (goal.current_value / goal.target_value) * 100;
  };

  // Filtra pela aba ativa e agrupa por período
  const visibleGoals = goals.filter(g => groupOfGoal(g) === activeTab);
  const groupedGoals = visibleGoals.reduce((acc, goal) => {
    const key = goal.period_type === 'anual' ? `Ano ${goal.year}` : `${goal.month}/${goal.year}`;
    if (!acc[key]) acc[key] = [];
    acc[key].push(goal);
    return acc;
  }, {});

  const accent = isOrcamento ? '#F59E0B' : '#22C55E';

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white">Metas &amp; Orçamento</h1>
            <p className="text-zinc-400 mt-2 text-sm md:text-base">
              {isOrcamento
                ? 'Defina tetos de gasto (mensais ou anuais) e não estoure o orçamento.'
                : 'Defina objetivos de resultado (mensais ou anuais) e acompanhe seu progresso.'}
            </p>
          </div>
          <Button data-testid="add-goal-button" onClick={openAdd} className="h-10" style={{ backgroundColor: accent }}>
            <Plus size={20} className="mr-2" />
            {isOrcamento ? 'Adicionar Orçamento' : 'Adicionar Meta'}
          </Button>
        </div>

        {/* Toggle Metas | Orçamento */}
        <div className="inline-flex rounded-xl bg-zinc-900 border border-white/10 p-1" data-testid="metas-orcamento-toggle">
          <button
            data-testid="tab-metas"
            onClick={() => setActiveTab('metas')}
            className={`flex items-center gap-2 px-5 h-10 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'metas' ? 'bg-[#22C55E] text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Target size={16} /> Metas
          </button>
          <button
            data-testid="tab-orcamento"
            onClick={() => setActiveTab('orcamento')}
            className={`flex items-center gap-2 px-5 h-10 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'orcamento' ? 'bg-[#F59E0B] text-black' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <Wallet size={16} /> Orçamento
          </button>
        </div>

        {/* Dialog */}
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold" style={{ color: accent }}>
                {editingGoal ? 'Editar' : (isOrcamento ? 'Novo' : 'Nova')} {isOrcamento ? 'Orçamento' : 'Meta'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label className="text-zinc-300">Nome</Label>
                <Input
                  data-testid="goal-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="input-field mt-1"
                  placeholder={isOrcamento ? 'Ex: Orçamento de Marketing' : 'Ex: Meta de Faturamento'}
                  required
                />
              </div>

              {/* Período: Mensal ou Anual */}
              <div>
                <Label className="text-zinc-300">Período</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    data-testid="period-mensal"
                    onClick={() => setFormData({ ...formData, period_type: 'mensal' })}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      formData.period_type === 'mensal'
                        ? 'bg-white/10 border-white/40 text-white'
                        : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    <CalendarDays size={18} /> <span className="font-medium text-sm">Mensal</span>
                  </button>
                  <button
                    type="button"
                    data-testid="period-anual"
                    onClick={() => setFormData({ ...formData, period_type: 'anual' })}
                    className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                      formData.period_type === 'anual'
                        ? 'bg-white/10 border-white/40 text-white'
                        : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    <CalendarRange size={18} /> <span className="font-medium text-sm">Anual</span>
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-300">Tipo</Label>
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="goal-type-input">
                    {!isOrcamento && META_TYPES.map(type => (
                      <button
                        key={type}
                        type="button"
                        data-testid={`goal-type-option-${type}`}
                        onClick={() => setFormData({ ...formData, goal_type: type })}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                          formData.goal_type === type ? 'text-black' : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20 hover:text-white'
                        }`}
                        style={formData.goal_type === type ? { backgroundColor: accent, borderColor: accent } : {}}
                      >
                        {type}
                      </button>
                    ))}
                    {isOrcamento && ORCAMENTO_TYPES.map(type => (
                      <button
                        key={type}
                        type="button"
                        data-testid={`goal-type-option-${type}`}
                        onClick={() => setFormData({ ...formData, goal_type: type })}
                        className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                          formData.goal_type === type ? 'text-black' : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20 hover:text-white'
                        }`}
                        style={formData.goal_type === type ? { backgroundColor: accent, borderColor: accent } : {}}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  {(() => {
                    const isAutoType = isFinancialGoalType(formData.goal_type);
                    const currentValue = getValueForGoalType(formData.goal_type, referenceValues);
                    const category = goalCategories.find(cat => cat.name === formData.goal_type);
                    const ref = category?.financial_reference || NAME_TO_REF[formData.goal_type];
                    const isPercentType = ref === 'roi' || ref === 'margem_lucro';
                    return (
                      <>
                        <Label className="text-zinc-300">Valor Atual {isAutoType && '(automático)'}</Label>
                        <Input
                          data-testid="current-value-input"
                          type="number"
                          step="0.01"
                          value={formData.current_value}
                          onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
                          className={`input-field mt-1 ${isAutoType ? 'bg-zinc-800 cursor-not-allowed' : ''}`}
                          placeholder={isAutoType ? 'Calculado automaticamente' : '0.00'}
                          readOnly={isAutoType}
                        />
                        {isAutoType && (
                          <p className="text-xs text-[#D4A43A] mt-1">
                            {loadingReference ? 'Carregando...' : currentValue !== null && currentValue !== undefined
                              ? `${formData.goal_type}: ${isPercentType ? `${currentValue}%` : `R$ ${Number(currentValue).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}`
                              : (formData.period_type === 'anual' ? 'Ano sem dados' : 'Selecione um mês')}
                          </p>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Direção */}
              <div>
                <Label className="text-zinc-300">Direção</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    data-testid="direction-progressiva"
                    onClick={() => setFormData({ ...formData, direction: 'progressiva' })}
                    className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                      formData.direction === 'progressiva'
                        ? 'bg-[#22C55E]/10 border-[#22C55E] text-[#22C55E]'
                        : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    <ArrowUp size={24} />
                    <span className="font-bold text-sm">Progressiva</span>
                    <span className="text-xs text-center opacity-70">Quer atingir ou ultrapassar</span>
                  </button>
                  <button
                    type="button"
                    data-testid="direction-decrescente"
                    onClick={() => setFormData({ ...formData, direction: 'decrescente' })}
                    className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
                      formData.direction === 'decrescente'
                        ? 'bg-[#EF4444]/10 border-[#EF4444] text-[#EF4444]'
                        : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20'
                    }`}
                  >
                    <ArrowDown size={24} />
                    <span className="font-bold text-sm">Decrescente</span>
                    <span className="text-xs text-center opacity-70">Quer ficar abaixo do limite</span>
                  </button>
                </div>
              </div>

              {/* Modo de valor: fixo ou porcentagem */}
              <div className="p-4 rounded-xl bg-white/5 border border-white/10 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Percent size={20} className={formData.is_percentage ? 'text-[#22C55E]' : 'text-zinc-500'} />
                    <div>
                      <Label className="text-white font-medium">Definir por Porcentagem</Label>
                      <p className="text-xs text-zinc-500">Ex: Folha máx. 35% do faturamento</p>
                    </div>
                  </div>
                  <Switch checked={formData.is_percentage} onCheckedChange={(checked) => setFormData({ ...formData, is_percentage: checked })} />
                </div>

                {formData.is_percentage ? (
                  <div className="space-y-4 pt-2 border-t border-white/10">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-300">{isROIReference ? 'Meta de ROI (%)' : 'Porcentagem (%)'}</Label>
                        <Input
                          data-testid="percentage-value-input"
                          type="number" step="0.1" min="0" max={isROIReference ? '1000' : '100'}
                          value={formData.percentage_value}
                          onChange={(e) => setFormData({ ...formData, percentage_value: e.target.value })}
                          className="input-field mt-1"
                          placeholder={isROIReference ? '10' : '35'}
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Base</Label>
                        <select
                          data-testid="reference-type-input"
                          value={formData.reference_type}
                          onChange={(e) => setFormData({ ...formData, reference_type: e.target.value })}
                          className="input-field w-full mt-1"
                        >
                          {referenceTypes.map(type => (<option key={type.value} value={type.value}>{type.label}</option>))}
                        </select>
                      </div>
                    </div>

                    {loadingReference ? (
                      <div className="flex items-center gap-2 text-zinc-400 text-sm">
                        <Loader2 size={14} className="animate-spin" /> Carregando valores de referência...
                      </div>
                    ) : referenceValues ? (
                      <div className="p-3 rounded-lg bg-black/30 space-y-2">
                        <p className="text-xs text-zinc-500 uppercase tracking-wider">
                          {formData.period_type === 'anual' ? `Valores do ano (${formData.year})` : `Valores do mês (${formData.month}/${formData.year})`}
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div className={`p-2 rounded ${formData.reference_type === 'faturamento' ? 'bg-[#22C55E]/10 border border-[#22C55E]/30' : ''}`}>
                            <span className="text-zinc-500">Faturamento:</span>
                            <p className="text-white font-bold">R$ {referenceValues.faturamento?.toFixed(2)}</p>
                          </div>
                          <div className={`p-2 rounded ${formData.reference_type === 'despesas' ? 'bg-[#EF4444]/10 border border-[#EF4444]/30' : ''}`}>
                            <span className="text-zinc-500">Despesas:</span>
                            <p className="text-white font-bold">R$ {referenceValues.despesas?.toFixed(2)}</p>
                          </div>
                          <div className={`p-2 rounded ${formData.reference_type === 'lucro' ? 'bg-[#10B981]/10 border border-[#10B981]/30' : ''}`}>
                            <span className="text-zinc-500">Lucro:</span>
                            <p className="text-white font-bold">R$ {referenceValues.lucro?.toFixed(2)}</p>
                          </div>
                          <div className={`p-2 rounded ${formData.reference_type === 'roi' ? 'bg-[#D4A43A]/10 border border-[#D4A43A]/30' : ''}`}>
                            <span className="text-zinc-500">ROI Atual:</span>
                            <p className="text-white font-bold">{referenceValues.roi?.toFixed(2)}%</p>
                          </div>
                        </div>
                        {calculatedTarget !== null && formData.percentage_value && (
                          <div className="mt-3 p-3 rounded-lg bg-[#22C55E]/10 border border-[#22C55E]/30">
                            <p className="text-xs text-[#22C55E] uppercase tracking-wider mb-1">Alvo Calculado</p>
                            <p className="text-2xl font-bold text-[#22C55E]">
                              {isROIReference ? `${calculatedTarget.toFixed(2)}%` : `R$ ${calculatedTarget.toFixed(2)}`}
                            </p>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-zinc-500">Selecione o período para ver os valores de referência</p>
                    )}
                  </div>
                ) : (
                  <div className="pt-2 border-t border-white/10">
                    <Label className="text-zinc-300">
                      {formData.direction === 'progressiva' ? 'Valor Alvo (mínimo)' : 'Valor Máximo (limite)'}
                    </Label>
                    <Input
                      data-testid="target-value-input"
                      type="number" step="0.01"
                      value={formData.target_value}
                      onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                      className="input-field mt-1"
                      placeholder="0.00"
                      required={!formData.is_percentage}
                    />
                  </div>
                )}
              </div>

              {/* Seleção de mês (só no período mensal) */}
              {formData.period_type === 'mensal' && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-zinc-300">{editingGoal ? 'Mês' : 'Meses (selecione um ou mais)'}</Label>
                    {!editingGoal && (
                      <button type="button" onClick={selectAllMonths} className="text-xs text-[#22C55E] hover:text-[#22C55E]/80">
                        {selectedMonths.length === months.length ? 'Desmarcar todos' : 'Selecionar todos'}
                      </button>
                    )}
                  </div>
                  {editingGoal ? (
                    <select
                      data-testid="month-input"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      className="input-field w-full"
                      required
                    >
                      {months.map(month => (
                        <option key={month} value={month}>{month.charAt(0).toUpperCase() + month.slice(1)}</option>
                      ))}
                    </select>
                  ) : (
                    <div className="grid grid-cols-4 gap-2">
                      {months.map(month => {
                        const isSelected = selectedMonths.includes(month);
                        return (
                          <button
                            key={month} type="button" onClick={() => toggleMonth(month)}
                            className={`p-2 rounded-lg border text-xs font-medium uppercase transition-all flex items-center justify-center gap-1 ${
                              isSelected ? 'bg-[#22C55E]/20 border-[#22C55E] text-[#22C55E]' : 'bg-white/5 border-white/10 text-zinc-400 hover:border-white/20 hover:text-white'
                            }`}
                          >
                            {isSelected && <Check size={12} />}{month.slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div>
                <Label className="text-zinc-300">Ano</Label>
                <Input
                  data-testid="year-input"
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                  className="input-field mt-1"
                  required
                />
              </div>

              <div>
                <Label className="text-zinc-300">Descrição (opcional)</Label>
                <Input
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="input-field mt-1"
                  placeholder="Detalhes"
                />
              </div>

              <Button data-testid="save-goal-button" type="submit" className="w-full" style={{ backgroundColor: accent, color: '#000' }} disabled={saving}>
                {saving ? (
                  <span className="flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Salvando...</span>
                ) : editingGoal ? 'Atualizar' : (formData.period_type === 'mensal' && selectedMonths.length > 1 ? `Criar ${selectedMonths.length}` : 'Salvar')}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Lista */}
        <Card data-testid="goals-list" className="glass-panel">
          <CardHeader>
            <CardTitle className="text-2xl font-heading text-white uppercase flex items-center gap-2">
              {isOrcamento ? <Wallet style={{ color: accent }} /> : <Target style={{ color: accent }} />}
              {isOrcamento ? 'Seus Orçamentos' : 'Suas Metas'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center text-zinc-500 py-8">Carregando...</p>
            ) : visibleGoals.length === 0 ? (
              <div className="text-center py-16">
                {isOrcamento ? <Wallet className="w-16 h-16 text-zinc-700 mx-auto mb-4" /> : <Target className="w-16 h-16 text-zinc-700 mx-auto mb-4" />}
                <p className="text-zinc-500 text-lg">{isOrcamento ? 'Nenhum orçamento cadastrado' : 'Nenhuma meta cadastrada'}</p>
                <p className="text-zinc-600 text-sm mt-2">
                  {isOrcamento ? 'Defina um teto de gasto (mensal ou anual) para começar' : 'Crie sua primeira meta (mensal ou anual) para acompanhar seu progresso'}
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {Object.entries(groupedGoals).map(([period, periodGoals]) => (
                  <div key={period}>
                    <h3 className="text-lg font-heading font-bold uppercase mb-4 flex items-center gap-2" style={{ color: accent }}>
                      {period.startsWith('Ano') ? <CalendarRange size={18} /> : <CalendarDays size={18} />}
                      {period}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {periodGoals.map(goal => {
                        const progress = calculateProgress(goal);
                        const isComplete = isGoalAchieved(goal);
                        const isDecrescente = (goal.direction || 'progressiva') === 'decrescente';
                        const isOverLimit = isDecrescente && goal.current_value > goal.target_value;
                        const isROIGoal = goal.reference_type === 'roi';
                        return (
                          <div
                            key={goal.id}
                            className={`p-6 rounded-lg border transition-all ${
                              isComplete ? 'bg-[#22C55E]/5 border-[#22C55E]/30'
                                : isOverLimit ? 'bg-[#EF4444]/5 border-[#EF4444]/30'
                                : 'bg-white/5 border-white/5 hover:border-white/20'
                            }`}
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className="px-2 py-1 rounded-md text-xs font-medium uppercase" style={{ backgroundColor: `${accent}1a`, color: accent }}>
                                    {goal.goal_type}
                                  </span>
                                  <span className="px-2 py-1 rounded-md bg-white/5 text-zinc-300 text-xs font-medium uppercase flex items-center gap-1">
                                    {goal.period_type === 'anual' ? <CalendarRange size={11} /> : <CalendarDays size={11} />}
                                    {goal.period_type === 'anual' ? 'Anual' : 'Mensal'}
                                  </span>
                                  <span className={`px-2 py-1 rounded-md text-xs font-medium uppercase flex items-center gap-1 ${isDecrescente ? 'bg-[#EF4444]/10 text-[#EF4444]' : 'bg-[#10B981]/10 text-[#10B981]'}`}>
                                    {isDecrescente ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                                    {isDecrescente ? 'Decrescente' : 'Progressiva'}
                                  </span>
                                  {isComplete && <span className="flex items-center gap-1 text-[#22C55E] text-xs font-bold"><TrendingUp size={14} />{isDecrescente ? 'Dentro' : 'Atingida!'}</span>}
                                  {isOverLimit && <span className="flex items-center gap-1 text-[#EF4444] text-xs font-bold"><TrendingDown size={14} />Estourou!</span>}
                                </div>
                                <h4 className="text-white font-bold text-lg">{goal.name}</h4>
                                {goal.description && <p className="text-zinc-400 text-sm mt-1">{goal.description}</p>}
                              </div>
                              <div className="flex gap-2 ml-2">
                                <button data-testid={`edit-goal-${goal.id}`} onClick={() => handleEdit(goal)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white transition-colors">
                                  <Pencil size={16} />
                                </button>
                                <button data-testid={`delete-goal-${goal.id}`} onClick={() => handleDelete(goal.id)} className="p-2 rounded-lg bg-white/5 hover:bg-red-500/10 text-zinc-400 hover:text-red-400 transition-colors">
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <div className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1">
                                <div className="flex items-baseline gap-2 flex-wrap min-w-0">
                                  <span className={`text-2xl sm:text-3xl font-bold font-numbers break-all ${isOverLimit ? 'text-[#EF4444]' : 'text-[#22C55E]'}`}>
                                    {isROIGoal ? `${goal.current_value.toFixed(1)}%` : `R$ ${goal.current_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                  </span>
                                  <span className="text-zinc-500 text-sm">/</span>
                                  <span className="text-zinc-400 font-numbers text-lg">
                                    {isDecrescente ? 'máx ' : ''}
                                    {isROIGoal ? `${goal.target_value.toFixed(1)}%` : `R$ ${goal.target_value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                  </span>
                                </div>
                                <span className={`text-sm font-medium ${isDecrescente ? (isOverLimit ? 'text-[#EF4444]' : 'text-[#22C55E]') : (isComplete ? 'text-[#22C55E]' : 'text-zinc-400')}`}>
                                  {isDecrescente ? `${progress.toFixed(1)}% do limite` : `${progress.toFixed(1)}%`}
                                </span>
                              </div>
                              <div className="w-full bg-zinc-800 rounded-full h-3 overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-500 ${isDecrescente ? (isOverLimit ? 'bg-[#EF4444]' : 'bg-gradient-to-r from-[#22C55E] to-[#10B981]') : (isComplete ? 'bg-[#22C55E]' : 'bg-gradient-to-r from-[#22C55E] to-[#10B981]')}`}
                                  style={{ width: `${Math.min(progress, 100)}%` }}
                                />
                              </div>
                              {isDecrescente && (
                                <p className="text-xs text-zinc-500 mt-1">
                                  {isOverLimit
                                    ? (isROIGoal ? `Ultrapassou em ${(goal.current_value - goal.target_value).toFixed(1)}%` : `Estourou em R$ ${(goal.current_value - goal.target_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`)
                                    : (isROIGoal ? `Margem de ${(goal.target_value - goal.current_value).toFixed(1)}% até o limite` : `Ainda pode gastar R$ ${(goal.target_value - goal.current_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`)}
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default GoalsPage;
